import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { cleaningAPI } from '../services/api';

// Public page a cleaner lands on from the Accept/Decline email link. The token
// in the URL is the credential — no login. Loading the offer is a GET (safe for
// email prefetch); Accept/Decline/mark-done happen only on a tap here.
const CleaningRespondPage = () => {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const intent = params.get('intent') || ''; // 'accept' | 'decline' | ''

  // missing | loading | open | submitting | complete | result | error
  const [phase, setPhase] = useState(token ? 'loading' : 'missing');
  const [offer, setOffer] = useState(null);
  const [result, setResult] = useState(null); // { state, cascaded }
  const [message, setMessage] = useState('');
  const [showDecline, setShowDecline] = useState(intent === 'decline');
  const [reason, setReason] = useState('');

  // completion form
  const [note, setNote] = useState('');
  const [photos, setPhotos] = useState([]); // File[]
  const [inventory, setInventory] = useState([]); // [{_id, name, category}]
  const [lowItems, setLowItems] = useState({}); // { itemId: true }

  // Load the property's inventory checklist once the cleaner is in the "mark
  // done" step, so they can flag anything running low.
  useEffect(() => {
    if (phase !== 'complete' || inventory.length > 0) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await cleaningAPI.getJobInventory(token);
        if (!cancelled) setInventory(Array.isArray(data?.items) ? data.items : []);
      } catch {
        /* inventory is optional — leave the checklist out if it can't load */
      }
    })();
    return () => { cancelled = true; };
  }, [phase, token, inventory.length]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await cleaningAPI.viewOffer(token);
        if (cancelled) return;
        setOffer(data);
        if (data.youAre === 'open') {
          setPhase('open');
        } else if ((data.youAre === 'assigned' || data.youAre === 'already_accepted') && !data.completed) {
          setPhase('complete');
        } else if (data.completed) {
          setResult({ state: 'completed' });
          setPhase('result');
        } else {
          setResult({ state: data.youAre });
          setPhase('result');
        }
      } catch (err) {
        if (cancelled) return;
        setPhase('error');
        setMessage(err?.response?.data?.detail || 'This link is invalid or has expired.');
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const respond = async (action) => {
    setPhase('submitting');
    try {
      const data = action === 'accept'
        ? await cleaningAPI.acceptOffer(token)
        : await cleaningAPI.declineOffer(token, reason);
      if (action === 'accept' && data.state === 'accepted') {
        setPhase('complete'); // offer the mark-done form straight away
        return;
      }
      setResult({ state: data.state, cascaded: data.cascaded, action });
      setPhase('result');
    } catch (err) {
      setPhase('error');
      setMessage(err?.response?.data?.detail || 'Something went wrong. Please try again.');
    }
  };

  const submitComplete = async () => {
    setPhase('submitting');
    try {
      const fd = new FormData();
      fd.append('notes', note);
      photos.slice(0, 4).forEach((f) => fd.append('files', f));
      const flaggedIds = Object.keys(lowItems).filter((id) => lowItems[id]);
      if (flaggedIds.length) fd.append('inventoryReport', JSON.stringify(flaggedIds));
      const data = await cleaningAPI.completeJob(token, fd);
      setResult({ state: data.state === 'already_completed' ? 'completed' : (data.state || 'completed') });
      setPhase('result');
    } catch (err) {
      setPhase('error');
      setMessage(err?.response?.data?.detail || 'Could not mark it done. Please try again.');
    }
  };

  const OfferCard = () => (
    <div className="bg-slate-50 rounded-xl p-5 text-left mb-6">
      <h2 className="text-lg font-bold text-[#1F3A68] mb-1">{offer?.propertyTitle}</h2>
      {offer?.propertyAddress ? (
        <p className="text-slate-500 text-sm mb-3">{offer.propertyAddress}</p>
      ) : null}
      <p className="text-slate-700 text-sm">
        <span className="text-slate-500">Clean after checkout:</span>{' '}
        <span className="font-semibold">{offer?.checkoutDate}</span>
      </p>
    </div>
  );

  const resultView = () => {
    const state = result?.state;
    const map = {
      completed: { icon: '✓', tone: 'emerald', title: 'Clean marked done',
        body: `Thanks — ${offer?.hostName || 'the host'} has been told the clean is complete.` },
      accepted: { icon: '✓', tone: 'emerald', title: "You're confirmed",
        body: `Thanks — ${offer?.hostName || 'the host'} has been told you'll take it. See you on ${offer?.checkoutDate}.` },
      already_accepted: { icon: '✓', tone: 'emerald', title: "You've already accepted this",
        body: `You're down to clean ${offer?.propertyTitle || 'this property'} after checkout on ${offer?.checkoutDate}.` },
      assigned: { icon: '✓', tone: 'emerald', title: "You've already accepted this",
        body: `You're down to clean ${offer?.propertyTitle || 'this property'} after checkout on ${offer?.checkoutDate}.` },
      declined: { icon: '↪', tone: 'slate', title: 'Thanks for letting us know',
        body: result?.cascaded
          ? "No problem — we've offered the job to another cleaner."
          : `We've let ${offer?.hostName || 'the host'} know you can't make this one.` },
      expired: { icon: '⌛', tone: 'slate', title: 'This offer has moved on',
        body: 'This job was passed to another cleaner because it wasn’t answered in time.' },
      skipped: { icon: '↪', tone: 'slate', title: 'This offer has moved on',
        body: 'This job has been offered to another cleaner.' },
      superseded: { icon: '↪', tone: 'slate', title: 'This offer has moved on',
        body: 'This job has already been offered to another cleaner.' },
      filled: { icon: '✓', tone: 'slate', title: 'This job is already taken',
        body: 'Another cleaner has accepted this one. Nothing more to do.' },
      closed: { icon: '—', tone: 'slate', title: 'This job is no longer available',
        body: 'This cleaning job has been closed.' },
      cancelled: { icon: '—', tone: 'slate', title: 'This job was cancelled',
        body: 'The host cancelled this cleaning job.' },
      exhausted: { icon: '—', tone: 'slate', title: 'This job is no longer available',
        body: 'This cleaning job has been closed.' },
    };
    const r = map[state] || map.closed;
    const tone = r.tone === 'emerald' ? 'text-emerald-600' : 'text-slate-500';
    return (
      <>
        <div className={`text-5xl mb-3 ${tone}`}>{r.icon}</div>
        <p className="text-[#1F3A68] font-semibold text-lg mb-2">{r.title}</p>
        <p className="text-slate-600 mb-6">{r.body}</p>
        <Link to="/" className="text-[#1F3A68] font-semibold hover:underline">About Ariv</Link>
      </>
    );
  };

  return (
    <div className="min-h-screen bg-[#F4F2E9] flex flex-col">
      <Header />
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center">
          {phase === 'missing' && (
            <>
              <h1 className="text-2xl font-bold text-[#1F3A68] mb-4">Cleaning job</h1>
              <p className="text-slate-600">This link is missing its token.</p>
            </>
          )}

          {(phase === 'loading' || phase === 'submitting') && (
            <p className="text-slate-600 py-6">{phase === 'loading' ? 'Loading the job…' : 'Saving…'}</p>
          )}

          {phase === 'open' && (
            <>
              <h1 className="text-2xl font-bold text-[#1F3A68] mb-1">
                Hi {offer?.cleanerName || 'there'} 👋
              </h1>
              <p className="text-slate-600 mb-5">
                {offer?.hostName || 'A host'} would like to book you for this turnover clean.
              </p>
              <OfferCard />

              {!showDecline ? (
                <div className="flex flex-col gap-3">
                  <button
                    onClick={() => respond('accept')}
                    className="w-full bg-emerald-600 text-white py-3 rounded-lg font-semibold hover:bg-emerald-700"
                  >
                    I can do it
                  </button>
                  <button
                    onClick={() => setShowDecline(true)}
                    className="w-full bg-white text-slate-600 border border-slate-300 py-3 rounded-lg font-semibold hover:bg-slate-50"
                  >
                    I'm not available
                  </button>
                </div>
              ) : (
                <div className="text-left">
                  <label className="block text-sm text-slate-600 mb-1">
                    Anything to tell the host? (optional)
                  </label>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={2}
                    className="w-full border border-slate-300 rounded-lg p-2 text-sm mb-3"
                    placeholder="e.g. already booked that day"
                  />
                  <div className="flex flex-col gap-3">
                    <button
                      onClick={() => respond('decline')}
                      className="w-full bg-slate-700 text-white py-3 rounded-lg font-semibold hover:bg-slate-800"
                    >
                      Send: I'm not available
                    </button>
                    <button
                      onClick={() => setShowDecline(false)}
                      className="w-full text-slate-500 py-2 font-medium hover:underline"
                    >
                      Back
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {phase === 'complete' && (
            <>
              <div className="text-emerald-600 text-4xl mb-2">✓</div>
              <h1 className="text-xl font-bold text-[#1F3A68] mb-1">You're confirmed</h1>
              <p className="text-slate-600 mb-5">
                When the clean is done, mark it here so {offer?.hostName || 'your host'} knows.
              </p>
              <OfferCard />
              <div className="text-left">
                <label className="block text-sm text-slate-600 mb-1">Add a note (optional)</label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={2}
                  className="w-full border border-slate-300 rounded-lg p-2 text-sm mb-3"
                  placeholder="e.g. all done, restocked toiletries"
                />
                <label className="block text-sm text-slate-600 mb-1">Add photos (optional, up to 4)</label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => setPhotos(Array.from(e.target.files || []).slice(0, 4))}
                  className="w-full text-sm mb-1"
                />
                {photos.length > 0 && (
                  <p className="text-xs text-slate-500 mb-3">{photos.length} photo(s) selected</p>
                )}

                {inventory.length > 0 && (
                  <div className="mt-3 mb-1">
                    <label className="block text-sm text-slate-600 mb-1">Anything running low? (optional)</label>
                    <p className="text-xs text-slate-400 mb-2">Tick anything that needs restocking — the host will be notified.</p>
                    <div className="max-h-44 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
                      {inventory.map((it) => (
                        <label key={it._id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
                          <input
                            type="checkbox"
                            checked={!!lowItems[it._id]}
                            onChange={(e) => setLowItems((prev) => ({ ...prev, [it._id]: e.target.checked }))}
                            className="w-4 h-4 rounded"
                          />
                          <span className="text-slate-700">{it.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <button
                  onClick={submitComplete}
                  className="w-full bg-[#1F3A68] text-white py-3 rounded-lg font-semibold hover:bg-[#1F3A68]/90 mt-2"
                >
                  Mark clean as done
                </button>
                <button
                  onClick={() => { setResult({ state: 'accepted' }); setPhase('result'); }}
                  className="w-full text-slate-500 py-2 mt-1 font-medium hover:underline"
                >
                  I'll do this later
                </button>
              </div>
            </>
          )}

          {phase === 'result' && resultView()}

          {phase === 'error' && (
            <>
              <div className="text-red-500 text-5xl mb-3">✕</div>
              <p className="text-red-700 mb-4">{message}</p>
              <Link to="/" className="text-[#1F3A68] font-semibold hover:underline">About Ariv</Link>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CleaningRespondPage;
